import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'pageTitle'
	| 'searchPlaceholder'
	| 'createSpaceButtonLabel'
	| 'emptyStateTitle'
	| 'emptyStateDescription'
	| 'viewRecommendedSpacesButtonLabel'
	| 'createNewSpaceButtonLabel'
	| 'searchEmptyStateTitle'
	| 'searchEmptyStateQuery'
	| 'errorTitle'
	| 'loadingLabel'
	| 'spaceChatsToggleLabel'
	| 'loadingChatsLabel'
	| 'chatsErrorTitle'
	| 'noChatsTitle'
	| 'untitledChat'
	| 'openChatLabel'
	| 'openChatInSpaceLabel'
	| 'chatCount'
	| 'sourceCount'
	| 'moreMenuDeleteLabel'
	| 'moreMenuRenameLabel'
	| 'moreMenuLabel'
	| 'cancelRenameLabel'
	| 'confirmRenameLabel'
	| 'renameErrorMessage';

const messages: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		pageTitle: {
			id: 'rovo-spaces.browse-spaces-fullscreen.pageTitle',
			defaultMessage: 'All spaces',
			description:
				'Page title shown at the top of the fullscreen "Browse spaces" view. Mirrors the heading shown above the search input and the flat list of the user\'s Rovo spaces.',
		},
		searchPlaceholder: {
			id: 'rovo-spaces.browse-spaces-fullscreen.searchPlaceholder',
			defaultMessage: 'Search spaces',
			description:
				'Placeholder text shown inside the search input on the fullscreen "Browse spaces" page. The input is currently non-functional and will be wired to a server-side search API in a follow-up change.',
		},
		createSpaceButtonLabel: {
			id: 'rovo-spaces.browse-spaces-fullscreen.createSpaceButtonLabel',
			defaultMessage: 'Create a space',
			description:
				'Label for the "Create a space" button shown next to the page title on the fullscreen "Browse spaces" page. Clicking the button opens the existing Create Space modal.',
		},
		emptyStateTitle: {
			id: 'rovo-spaces.browse-spaces-fullscreen.emptyStateTitle',
			defaultMessage: 'No spaces yet',
			description:
				'Empty-state heading shown on the fullscreen "Browse spaces" page when the current user has no Rovo spaces AND no search query is active.',
		},
		emptyStateDescription: {
			id: 'rovo-spaces.browse-spaces-fullscreen.emptyStateDescription',
			defaultMessage:
				'Collect team or project context in spaces, so that your chat answers come from documents, links, and other information that matter to you, not just general knowledge.',
			description:
				'Empty-state body copy shown on the fullscreen "Browse spaces" page when the current user has no Rovo spaces. Explains that spaces collect context so Rovo answers can use relevant documents, links, and information.',
		},
		viewRecommendedSpacesButtonLabel: {
			id: 'rovo-spaces.browse-spaces-fullscreen.viewRecommendedSpacesButtonLabel',
			defaultMessage: 'View recommended spaces',
			description:
				'Primary CTA label in the no-spaces empty state. Clicking it takes the user to the recommended spaces view.',
		},
		createNewSpaceButtonLabel: {
			id: 'rovo-spaces.browse-spaces-fullscreen.createNewSpaceButtonLabel',
			defaultMessage: 'Create new space',
			description:
				'Secondary CTA label in the no-spaces empty state. Clicking it opens the Create Space modal.',
		},
		searchEmptyStateTitle: {
			id: 'rovo-spaces.browse-spaces-fullscreen.searchEmptyStateTitle',
			defaultMessage: 'No results found for',
			description:
				'Empty-state heading shown on the fullscreen "Browse spaces" page when the current user has active search query text but the results came back empty.',
		},
		searchEmptyStateQuery: {
			id: 'rovo-spaces.browse-spaces-fullscreen.searchEmptyStateQuery',
			defaultMessage: '\u201c{query}\u201d',
			description:
				'Search query displayed in quotes below the no-results heading on the fullscreen "Browse spaces" page. The `query` placeholder is the user-typed search string.',
		},
		errorTitle: {
			id: 'rovo-spaces.browse-spaces-fullscreen.errorTitle',
			defaultMessage: 'We couldn\u2019t load your spaces',
			description:
				'Error message shown on the fullscreen "Browse spaces" page when the underlying spaces list request fails.',
		},
		loadingLabel: {
			id: 'rovo-spaces.browse-spaces-fullscreen.loadingLabel',
			defaultMessage: 'Loading spaces\u2026',
			description:
				'Accessible loading label shown while the fullscreen "Browse spaces" page is fetching the user\'s spaces.',
		},
		spaceChatsToggleLabel: {
			id: 'rovo-spaces.browse-spaces-fullscreen.spaceChatsToggleLabel',
			defaultMessage: 'Chats in {spaceName}',
			description:
				'Accessible label for the icon-only button that expands or collapses the chats inside a space row on the "Browse spaces" page. `spaceName` is the name of the space. The expanded or collapsed state is announced separately by the button.',
		},
		loadingChatsLabel: {
			id: 'rovo-spaces.browse-spaces-fullscreen.loadingChatsLabel',
			defaultMessage: 'Loading chats\u2026',
			description:
				'Accessible loading label shown inside an expanded space row while the "Browse spaces" page is fetching chats for that space.',
		},
		chatsErrorTitle: {
			id: 'rovo-spaces.browse-spaces-fullscreen.chatsErrorTitle',
			defaultMessage: 'We couldn\u2019t load chats',
			description:
				'Error message shown inside an expanded space row on the "Browse spaces" page when the chats for that space fail to load.',
		},
		noChatsTitle: {
			id: 'rovo-spaces.browse-spaces-fullscreen.noChatsTitle',
			defaultMessage: 'There are no chats in this space yet',
			description:
				'Empty-state message shown inside an expanded space row on the "Browse spaces" page when the space has no chats.',
		},
		untitledChat: {
			id: 'rovo-spaces.browse-spaces-fullscreen.untitledChat',
			defaultMessage: 'Untitled chat',
			description:
				'Fallback title shown for a chat inside an expanded space row when the conversation does not have a name.',
		},
		openChatLabel: {
			id: 'rovo-spaces.browse-spaces-fullscreen.openChatLabel',
			defaultMessage: 'Open chat {name}',
			description:
				'Accessible label for a chat row inside an expanded space row on the "Browse spaces" page. `name` is the chat title.',
		},
		openChatInSpaceLabel: {
			id: 'rovo-spaces.browse-spaces-fullscreen.openChatInSpaceLabel',
			defaultMessage: 'Open chat {name} in {spaceName}',
			description:
				'Accessible label for a chat search result on the "Browse spaces" page. `name` is the chat title and `spaceName` is the parent space name, included to distinguish chats with similar names.',
		},
		chatCount: {
			id: 'rovo-spaces.browse-spaces-fullscreen.chatCount',
			defaultMessage: '{count, plural, one {# chat} other {# chats}}',
			description:
				'Secondary metadata on a space row showing how many chats are associated with the space. `count` is the total number of unique chats (integer).',
		},
		sourceCount: {
			id: 'rovo-spaces.browse-spaces-fullscreen.sourceCount',
			defaultMessage: '{count, plural, one {# source} other {# sources}}',
			description:
				'Secondary metadata on a space row showing how many knowledge sources the space contains. `count` is the total number of sources (integer).',
		},
		moreMenuDeleteLabel: {
			id: 'rovo-spaces.browse-spaces-fullscreen.moreMenuDeleteLabel',
			defaultMessage: 'Delete',
			description:
				'Menu item label in the Browse spaces row three-dot more menu. Selecting this opens a confirmation modal to delete the space.',
		},
		moreMenuRenameLabel: {
			id: 'rovo-spaces.browse-spaces-fullscreen.moreMenuRenameLabel',
			defaultMessage: 'Rename',
			description:
				'Menu item label in the Browse spaces row three-dot more menu. Selecting this lets the user rename the space inline.',
		},
		moreMenuLabel: {
			id: 'rovo-spaces.browse-spaces-fullscreen.moreMenuLabel',
			defaultMessage: 'More options',
			description: 'Accessible label for the three-dot more options button on a Browse spaces row.',
		},
		cancelRenameLabel: {
			id: 'rovo-spaces.browse-spaces-fullscreen.cancelRenameLabel',
			defaultMessage: 'Cancel rename',
			description:
				'Accessible label for the cancel button in the inline space rename form on the Browse spaces page.',
		},
		confirmRenameLabel: {
			id: 'rovo-spaces.browse-spaces-fullscreen.confirmRenameLabel',
			defaultMessage: 'Confirm rename',
			description:
				'Accessible label for the confirm button in the inline space rename form on the Browse spaces page.',
		},
		renameErrorMessage: {
			id: 'rovo-spaces.browse-spaces-fullscreen.renameErrorMessage',
			defaultMessage: 'We couldn\u2019t rename this space. Try again.',
			description:
				'Inline error message shown below the text field when renaming a space from the Browse spaces page fails. It tells the user the rename was not saved and prompts them to retry.',
		},
	});

export default messages;
